import type { FormEmbedField } from "./types.js";

/**
 * Maps a customer-authored field to the HTML `autocomplete` token that lets
 * iOS/Android offer one-tap contact autofill.
 *
 * Field names come from each customer's form builder, so they arrive in every
 * shape a human might type (`first_name`, `firstName`, `fname`, `First Name`).
 * We normalize away case and separators, then match the field's `name` first
 * and its `label` second — the name is the stabler signal, but plenty of forms
 * use opaque names (`q1`) with a descriptive label.
 *
 * Returns `undefined` when nothing matches, which renders no attribute at all.
 * That is deliberate: omitting `autocomplete` leaves the browser's own
 * heuristics intact, whereas `autocomplete="off"` suppresses autofill outright.
 */

function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Ordered most-specific first: `firstname` has to win before the bare `name`
 * rule can claim it, and `zipcode` before any looser `code` match.
 *
 * Every pattern is anchored at both ends. A loose one does real damage here —
 * a prefix match on `tel` would put the visitor's phone number into a
 * `telephone_extension` box, and a substring match on `email` would put their
 * address into `email_subject`. An unmatched field just gets no attribute,
 * so the cost of being too strict is far lower than the cost of guessing.
 */
const RULES: Array<{ match: RegExp; token: string }> = [
  { match: /^(first|given|fore)name$|^fname$/, token: "given-name" },
  { match: /^(last|family|sur)name$|^lname$/, token: "family-name" },
  { match: /^(middle)name$|^mname$/, token: "additional-name" },
  { match: /^(full|your|contact|customer|client)?name$/, token: "name" },

  { match: /^(e?mail|emailaddress)$/, token: "email" },
  { match: /^(tel|telephone|phone|phonenumber|telephonenumber|mobile|mobilenumber|mobilephone|cell|cellphone|cellnumber|contactnumber)$/, token: "tel" },

  { match: /^(company|business|organi[sz]ation|orgname|businessname|companyname)$/, token: "organization" },

  { match: /^(address|street|streetaddress|addressline1|address1)$/, token: "address-line1" },
  { match: /^(address2|addressline2|apt|apartment|unit|suite)$/, token: "address-line2" },
  { match: /^(city|town|locality)$/, token: "address-level2" },
  { match: /^(state|province|region)$/, token: "address-level1" },
  { match: /^(zip|zipcode|postal|postalcode|postcode)$/, token: "postal-code" },
  { match: /^(country|countryname)$/, token: "country-name" },
];

function tokenFor(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const key = normalize(value);
  if (!key) return undefined;
  return RULES.find((rule) => rule.match.test(key))?.token;
}

/**
 * The `autocomplete` value for a field, or `undefined` to render no attribute.
 * Typed fields short-circuit: an `email`/`phone` field is what it says it is,
 * whatever the customer named it.
 */
export function autocompleteFor(field: FormEmbedField): string | undefined {
  if (field.type === "email") return "email";
  if (field.type === "phone") return "tel";

  // Free-text answers ("what needs cleaning?") have no contact semantics, and
  // a wrong guess here would autofill a name into a description box.
  if (field.type === "textarea") return undefined;

  return tokenFor(field.name) ?? tokenFor(field.label);
}

/**
 * `enterkeyhint` labels the mobile keyboard's action key. The last field gets
 * "go" (submit is the next thing they want), everything else gets "next".
 */
export function enterKeyHintFor(isLast: boolean): "go" | "next" {
  return isLast ? "go" : "next";
}
